/**
 * Aba "Raios" da barra lateral: liga a camada, diz quantos raios há no
 * quadro exibido e explica de onde vêm e para que servem na denúncia.
 */
import { CircleQuestionMark, ExternalLink, RefreshCw, Zap } from 'lucide-react';
import { formatInt } from '../lib/format';
import { formatRelative, formatTimeBrt } from '../lib/time';
import { shownTimeOf, useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { Button, Hint, ProgressBar, Section, Toggle } from '../ui/primitives';
import { BASE_RAIOS, HORAS_CONSULTA, NOAA_GLM_URL, RAIO_CONSULTA_KM } from './api';
import { COR_RAIO } from './layer';
import { useRaiosStore } from './store';
import { carregarRaios, ultimoQuadro } from './sync';
import { avisoDeFrescor, quadroParaInstante } from './tempo';

function Legenda() {
  return (
    <div className="flex items-center gap-2 text-xs text-ink-200">
      <svg width="16" height="16" viewBox="-8 -8 16 16" aria-hidden className="shrink-0">
        <path d="M0 -6 L1.7 -1.7 L6 0 L1.7 1.7 L0 6 L-1.7 1.7 L-6 0 L-1.7 -1.7 Z" fill={COR_RAIO} stroke="rgba(30,20,0,0.75)" strokeWidth="1" />
      </svg>
      Um raio (nuvem-solo ou dentro da nuvem) nos 10 min do quadro
    </div>
  );
}

export function RaiosPanel() {
  const visivel = useRaiosStore((s) => s.visivel);
  const situacao = useRaiosStore((s) => s.situacao);
  const indice = useRaiosStore((s) => s.indice);
  const noQuadro = useRaiosStore((s) => s.noQuadro);
  const progresso = useRaiosStore((s) => s.progresso);
  const { setVisivel } = useRaiosStore.getState();
  const now = useNow(60_000);
  const instante = useTimeStore((s) => shownTimeOf(s));
  const quadros = indice?.quadros ?? [];
  const q = quadroParaInstante(
    quadros,
    quadros.map((x) => x.t),
    instante,
  );
  const ultimo = ultimoQuadro(indice);
  const aviso = avisoDeFrescor(ultimo, now);
  const baixando = progresso.total > 0 && progresso.baixados + progresso.falhas < progresso.total;

  if (!BASE_RAIOS) {
    return (
      <Section title="Raios" icon={Zap}>
        <Hint>A camada de raios está desligada nesta versão.</Hint>
      </Section>
    );
  }

  const frase = !q
    ? 'Sem dado do GLM neste horário.'
    : noQuadro.estado === 'carregando'
      ? 'Baixando os raios deste horário…'
      : noQuadro.estado === 'falhou'
        ? 'Os raios deste horário não carregaram.'
        : q.n === 0
          ? 'Nenhum raio neste horário.'
          : `${formatInt(q.n)} ${q.n === 1 ? 'raio' : 'raios'} em 10 min.`;

  return (
    <div>
      <Section title="Raios" icon={Zap}>
        <Toggle
          label="Raios vistos pelo GOES-19"
          checked={visivel}
          onChange={setVisivel}
          description="Cada raio detectado pelo mapeador de raios do satélite, a cada 10 min, animado junto com a imagem."
        />
        {visivel && aviso && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">{aviso}</div>
        )}
        {visivel && situacao.estado === 'error' && !indice && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            Não deu para baixar a lista de quadros dos raios: {situacao.erro}
          </div>
        )}
        {visivel && (
          <>
            <Legenda />
            {indice && (
              <div className="rounded-lg border border-ink-700/70 bg-ink-800/50 px-2.5 py-1.5 text-xs leading-snug text-ink-200">
                {q && <span className="mr-1 font-medium text-white tabular-nums">{formatTimeBrt(q.t)}</span>}
                {frase}
              </div>
            )}
            {baixando && (
              <div className="space-y-1">
                <ProgressBar value={progresso.total ? progresso.baixados / progresso.total : 0} tone="fogo" />
                <div className="text-[11px] text-ink-300 tabular-nums">
                  Baixando os raios: {formatInt(progresso.baixados)} de {formatInt(progresso.total)}
                </div>
              </div>
            )}
            <Hint>
              Na ficha de cada foco aparece se houve raio a até {RAIO_CONSULTA_KM} km nas {HORAS_CONSULTA} h antes dele, mesmo com esta camada desligada.
            </Hint>
          </>
        )}
      </Section>

      <Section title="Dados" icon={RefreshCw} defaultOpen={false} storageKey="raios-dados">
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando a lista de quadros…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao atualizar: {situacao.erro}</div>}
          {indice && (
            <div>
              {formatInt(quadros.length)} quadros nos últimos 7 dias
              {ultimo !== null && <> · o mais novo é de {formatTimeBrt(ultimo)} ({formatRelative(ultimo, now)})</>}
            </div>
          )}
          <Button icon={RefreshCw} onClick={() => void carregarRaios(true)}>
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false} storageKey="raios-oque">
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            O GOES-19 leva um mapeador de raios, o GLM: uma câmera que olha o continente o tempo todo e registra o clarão de cada
            raio, de dia e de noite, dentro da nuvem ou até o chão. A posição tem precisão de alguns quilômetros.
          </p>
          <p>
            <strong>Para que serve numa denúncia:</strong> raio é a única causa natural comum de fogo na Amazônia. Se não caiu raio
            perto do foco nos dias antes dele, o fogo muito provavelmente foi aceso por alguém. A ficha do foco e a página da
            denúncia mostram isso sozinhas.
          </p>
          <p>
            O satélite não vê todos os raios: a NOAA garante mais de 70% na média, e estudos na América do Sul medem entre 75% e
            90%. Por isso a conta usa um raio largo ({RAIO_CONSULTA_KM} km) e vários dias ({HORAS_CONSULTA} h).
          </p>
          <p className="text-ink-400">
            Fonte: NOAA — GOES-19 GLM, produto de flashes (GLM-L2-LCFA), sem filtro. O AmaView guarda os últimos 7 dias.{' '}
            <a href={NOAA_GLM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-fogo-400 hover:underline">
              sobre o GLM
              <ExternalLink size={10} />
            </a>
          </p>
        </div>
      </Section>
    </div>
  );
}
